import { useCallback, useEffect, useMemo, useState } from 'react'
import { Gift, Copy, Check, ExternalLink, Download } from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../lib/api'
import { useAppLanguage } from '../../i18n'
import MarkdownMessage from '../../components/MarkdownMessage'
import { HelpTooltip } from '../../components/Tooltip'

type Me = {
  public_code: string | null
  referral_link: string | null
  referrals_count: number
  commissions_payable_cents: number
  commissions_pending_hold_cents: number
  commissions_paid_cents: number
  commissions_currency: string
  commission_rate_bps: number
  recurring_commissions: boolean
  payable_after_referral_days: number
  affiliate_contract_required: boolean
  affiliate_contract_accepted: boolean
  affiliate_contract_version: string
}

type CommissionRow = {
  public_id: string
  referred_user_id: number
  commission_amount_cents: number
  currency: string
  status: string
  plan_slug: string | null
  stripe_invoice_id: string
  created_at: string | null
  paid_at: string | null
  payable_at: string | null
}

type ReferralRow = {
  referred_user_id: number
  attributed_at: string | null
  source: string
  commissions_count: number
  total_commission_cents: number
  last_commission_at: string | null
  has_paid_commission: boolean
}

type MonthlyPoint = {
  month_start: string
  commission_amount_cents: number
}

type MonthlyLinkViewPoint = {
  month_start: string
  view_count: number
}

type ReferrerDomainRow = {
  domain: string
  view_count: number
}

type AffiliateUserStats = {
  referrals_attributed_total: number
  referred_with_commission_count: number
  monthly_commissions: MonthlyPoint[]
  link_views_total: number
  link_views_last_30_days: number
  monthly_link_views: MonthlyLinkViewPoint[]
  top_referrer_domains: ReferrerDomainRow[]
}

type ContractState = {
  version: string
  markdown: string
  contract_required: boolean
  needs_acceptance: boolean
}

export default function AccountAffiliatePage() {
  const { t } = useAppLanguage()
  const [me, setMe] = useState<Me | null>(null)
  const [rows, setRows] = useState<CommissionRow[]>([])
  const [referrals, setReferrals] = useState<ReferralRow[]>([])
  const [referralsTotal, setReferralsTotal] = useState(0)
  const [dashStats, setDashStats] = useState<AffiliateUserStats | null>(null)
  const [contract, setContract] = useState<ContractState | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [copied, setCopied] = useState<'link' | 'code' | null>(null)
  const [acceptChecked, setAcceptChecked] = useState(false)
  const [acceptSubmitting, setAcceptSubmitting] = useState(false)

  const cur = me?.commissions_currency || 'EUR'

  const load = useCallback(async () => {
    setErr(null)
    setLoading(true)
    try {
      const [m, c, ct] = await Promise.all([
        api.get<Me>('/affiliate/me'),
        api.get<CommissionRow[]>('/affiliate/me/commissions'),
        api.get<ContractState>('/affiliate/contract'),
      ])
      setMe(m.data)
      setRows(Array.isArray(c.data) ? c.data : [])
      setContract(ct.data)
      setAcceptChecked(false)

      // 404 = backend pas encore redémarré après ajout des routes ; 503 = programme désactivé.
      // validateStatus évite un reject axios → pas de console.error dans api.ts.
      const optionalAffiliate = (status: number) =>
        (status >= 200 && status < 300) || status === 404 || status === 503

      try {
        const [refRes, stRes] = await Promise.all([
          api.get<{ items: ReferralRow[]; total: number }>('/affiliate/me/referrals', {
            params: { skip: 0, limit: 200 },
            validateStatus: optionalAffiliate,
          }),
          api.get<AffiliateUserStats>('/affiliate/me/stats', {
            validateStatus: optionalAffiliate,
          }),
        ])
        if (refRes.status === 200) {
          const payload = refRes.data
          setReferrals(Array.isArray(payload?.items) ? payload.items : [])
          setReferralsTotal(typeof payload?.total === 'number' ? payload.total : 0)
        } else {
          setReferrals([])
          setReferralsTotal(0)
        }
        if (stRes.status === 200) {
          const d = stRes.data
          setDashStats({
            ...d,
            referrals_attributed_total: d.referrals_attributed_total ?? 0,
            referred_with_commission_count: d.referred_with_commission_count ?? 0,
            monthly_commissions: Array.isArray(d.monthly_commissions) ? d.monthly_commissions : [],
            link_views_total: d.link_views_total ?? 0,
            link_views_last_30_days: d.link_views_last_30_days ?? 0,
            monthly_link_views: Array.isArray(d.monthly_link_views) ? d.monthly_link_views : [],
            top_referrer_domains: Array.isArray(d.top_referrer_domains) ? d.top_referrer_domains : [],
          })
        } else {
          setDashStats(null)
        }
      } catch {
        setReferrals([])
        setReferralsTotal(0)
        setDashStats(null)
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const acceptContract = async () => {
    if (!contract || !acceptChecked) return
    setAcceptSubmitting(true)
    setErr(null)
    try {
      await api.post('/affiliate/contract/accept', { version: contract.version })
      await load()
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setAcceptSubmitting(false)
    }
  }

  const copy = async (text: string, kind: 'link' | 'code') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(kind)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      /* ignore */
    }
  }

  const fmt = (cents: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(cents / 100)

  const exportReferralsCsv = async () => {
    setExporting(true)
    setErr(null)
    try {
      const res = await api.get<Blob>('/affiliate/me/referrals/export.csv', { responseType: 'blob' })
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'totemium-affiliate-referrals.csv'
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } }
      setErr(ax.response?.data?.detail || 'Error')
    } finally {
      setExporting(false)
    }
  }

  const chartData = useMemo(() => {
    const mc = dashStats?.monthly_commissions
    if (!Array.isArray(mc) || mc.length === 0) return []
    return mc.map((p) => {
      const d = new Date(p.month_start)
      const label = Number.isNaN(d.getTime())
        ? p.month_start.slice(0, 7)
        : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
      return {
        label,
        commission_amount_cents: p.commission_amount_cents,
      }
    })
  }, [dashStats])

  const linkViewsChartData = useMemo(() => {
    const mlv = dashStats?.monthly_link_views
    if (!Array.isArray(mlv) || mlv.length === 0) return []
    return mlv.map((p) => {
      const d = new Date(p.month_start)
      const label = Number.isNaN(d.getTime())
        ? p.month_start.slice(0, 7)
        : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
      return { label, view_count: p.view_count }
    })
  }, [dashStats])

  if (loading) {
    return <p className="text-zinc-500 dark:text-zinc-400">{t.common.loading}</p>
  }
  if (err && !me) {
    return <p className="text-red-600 dark:text-red-400">{err}</p>
  }

  const showContractGate = contract?.needs_acceptance && contract.contract_required

  return (
    <div className="space-y-8">
      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

      {showContractGate && contract && (
        <div className="rounded-3xl border border-amber-200/80 bg-amber-50/40 p-6 dark:border-amber-900/50 dark:bg-amber-950/20">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{t.affiliate.contractTitle}</h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{t.affiliate.contractSubtitle}</p>
          <p className="mt-1 text-xs text-zinc-500">
            {t.affiliate.contractVersionLabel}: <strong>{contract.version}</strong>
          </p>
          <div className="mt-4 max-h-[min(420px,50vh)] overflow-y-auto rounded-2xl border border-zinc-200 bg-white/90 p-4 text-sm dark:border-zinc-700 dark:bg-zinc-900/80">
            <MarkdownMessage content={contract.markdown || t.affiliate.contractEmpty} />
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
            <input
              type="checkbox"
              className="mt-1"
              checked={acceptChecked}
              onChange={(e) => setAcceptChecked(e.target.checked)}
            />
            <span>{t.affiliate.contractAcceptCheckbox}</span>
          </label>
          <button
            type="button"
            disabled={!acceptChecked || acceptSubmitting}
            onClick={() => void acceptContract()}
            className="mt-4 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {acceptSubmitting ? t.common.loading : t.affiliate.contractAcceptButton}
          </button>
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-emerald-500/15 p-3 text-emerald-600 dark:text-emerald-400">
          <Gift className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">{t.affiliate.title}</h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{t.affiliate.subtitle}</p>
          <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-500">{t.affiliate.dashboardPrivacyNote}</p>
          {me && (
            <p className="mt-2 text-xs text-zinc-500">
              {(me.commission_rate_bps / 100).toFixed(2)}% ·{' '}
              {me.recurring_commissions ? t.affiliate.recurringOn : t.affiliate.recurringOff}
              {me.payable_after_referral_days > 0 ? (
                <>
                  {' '}
                  · {t.affiliate.holdPeriodNote.replace('{days}', String(me.payable_after_referral_days))}
                </>
              ) : null}
            </p>
          )}
        </div>
      </div>

      {me?.public_code && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-3xl border border-emerald-100/80 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t.affiliate.yourCode}</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded-xl bg-zinc-100 px-3 py-2 font-mono text-sm dark:bg-zinc-800">
                {me.public_code}
              </code>
              <button
                type="button"
                onClick={() => copy(me.public_code!, 'code')}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
              >
                {copied === 'code' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied === 'code' ? t.affiliate.copied : t.affiliate.copy}
              </button>
            </div>
          </div>
          {me.referral_link && (
            <div className="rounded-3xl border border-emerald-100/80 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{t.affiliate.yourLink}</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="flex-1 truncate text-sm text-zinc-700 dark:text-zinc-300">{me.referral_link}</span>
                <button
                  type="button"
                  onClick={() => copy(me.referral_link!, 'link')}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
                >
                  {copied === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied === 'link' ? t.affiliate.copied : t.affiliate.copy}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!showContractGate && me && !me.public_code && me.affiliate_contract_required && !me.affiliate_contract_accepted ? (
        <p className="text-sm text-zinc-600 dark:text-zinc-400">{t.affiliate.linkHiddenUntilContract}</p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-3xl border border-emerald-100/70 bg-emerald-50/40 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">{t.affiliate.referrals}</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">{me?.referrals_count ?? 0}</p>
        </div>
        <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">{t.affiliate.payable}</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-700 dark:text-emerald-400">
            {fmt(me?.commissions_payable_cents ?? 0, cur)}
          </p>
        </div>
        <div className="rounded-3xl border border-amber-100/70 bg-amber-50/30 p-4 dark:border-amber-900/40 dark:bg-amber-950/10">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">{t.affiliate.pendingHold}</p>
          <p className="mt-1 text-2xl font-semibold text-amber-800 dark:text-amber-300">
            {fmt(me?.commissions_pending_hold_cents ?? 0, cur)}
          </p>
          <p className="mt-1 text-xs text-zinc-500">{t.affiliate.pendingHoldHint}</p>
        </div>
        <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
          <p className="text-xs text-zinc-500 dark:text-zinc-400">{t.affiliate.paid}</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">
            {fmt(me?.commissions_paid_cents ?? 0, cur)}
          </p>
        </div>
      </div>

      {dashStats && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <p className="text-xs text-zinc-500">{t.affiliate.statsAttributedLabel}</p>
            <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">
              {dashStats.referrals_attributed_total}
            </p>
          </div>
          <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
            <p className="text-xs text-zinc-500">{t.affiliate.statsWithCommissionLabel}</p>
            <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">
              {dashStats.referred_with_commission_count}
            </p>
          </div>
        </div>
      )}

      {dashStats && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{t.affiliate.linkViewsSectionTitle}</h2>
            <p className="mt-1 text-xs text-zinc-500">{t.affiliate.linkViewsHint}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
              <p className="text-xs text-zinc-500">{t.affiliate.linkViewsTotal}</p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">
                {dashStats.link_views_total ?? 0}
              </p>
            </div>
            <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
              <p className="text-xs text-zinc-500">{t.affiliate.linkViewsLast30}</p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">
                {dashStats.link_views_last_30_days ?? 0}
              </p>
            </div>
          </div>

          {(linkViewsChartData?.length ?? 0) > 0 && (
            <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
              <h3 className="mb-4 text-base font-semibold text-zinc-900 dark:text-white">{t.affiliate.linkViewsChartTitle}</h3>
              <div className="h-[220px] w-full min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={linkViewsChartData ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'currentColor' }} className="text-zinc-500" />
                    <YAxis
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      className="text-zinc-500"
                      allowDecimals={false}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: '1rem',
                        border: '1px solid rgb(16 185 129 / 0.25)',
                      }}
                      formatter={(value: unknown) => {
                        const n = typeof value === 'number' ? value : Number(value)
                        return [Number.isFinite(n) ? n : 0, t.affiliate.linkViewsShort]
                      }}
                      labelFormatter={(l) => (l == null ? '' : String(l))}
                    />
                    <Bar dataKey="view_count" name={t.affiliate.linkViewsShort} fill="rgb(16 185 129)" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {(dashStats.top_referrer_domains ?? []).length > 0 && (
            <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
              <h3 className="mb-3 text-base font-semibold text-zinc-900 dark:text-white">{t.affiliate.topReferrersTitle}</h3>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[360px] text-left text-sm">
                  <thead className="border-b border-emerald-100/80 dark:border-emerald-900/40">
                    <tr>
                      <th className="py-2 pr-4 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.columnReferrerDomain}</th>
                      <th className="py-2 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.columnViewCount}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(dashStats.top_referrer_domains ?? []).map((row, idx) => (
                      <tr key={`${row.domain}-${idx}`} className="border-b border-zinc-100 dark:border-zinc-800">
                        <td className="py-2 pr-4 font-mono text-xs text-zinc-800 dark:text-zinc-200">{row.domain}</td>
                        <td className="py-2 text-zinc-700 dark:text-zinc-300">{row.view_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {chartData.length > 0 && (
        <div className="rounded-3xl border border-emerald-100/70 bg-white/60 p-4 dark:border-emerald-900/40 dark:bg-zinc-900/40">
          <h2 className="mb-4 text-lg font-semibold text-zinc-900 dark:text-white">{t.affiliate.chartTitle}</h2>
          <div className="h-[260px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="affCommFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'currentColor' }} className="text-zinc-500" />
                <YAxis
                  tick={{ fontSize: 11, fill: 'currentColor' }}
                  className="text-zinc-500"
                  tickFormatter={(v) =>
                    new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: cur,
                      maximumFractionDigits: 0,
                    }).format((v as number) / 100)
                  }
                />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: '1rem',
                    border: '1px solid rgb(16 185 129 / 0.25)',
                  }}
                  formatter={(value: unknown) => {
                    const n = typeof value === 'number' ? value : Number(value)
                    const cents = Number.isFinite(n) ? n : 0
                    return [fmt(cents, cur), t.affiliate.amount]
                  }}
                  labelFormatter={(l) => (l == null ? '' : String(l))}
                />
                <Area
                  type="monotone"
                  dataKey="commission_amount_cents"
                  name={t.affiliate.amount}
                  stroke="rgb(16 185 129)"
                  strokeWidth={2}
                  fill="url(#affCommFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {dashStats && chartData.length === 0 && (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{t.affiliate.chartEmpty}</p>
      )}

      <div>
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">{t.affiliate.referralsSectionTitle}</h2>
          <button
            type="button"
            disabled={exporting || referralsTotal === 0}
            onClick={() => void exportReferralsCsv()}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/50"
          >
            <Download className="h-4 w-4" />
            {exporting ? t.common.loading : t.affiliate.exportReferralsCsv}
          </button>
        </div>
        <p className="mb-3 text-xs text-zinc-500">{t.affiliate.referredUserIdHint}</p>
        {(referrals ?? []).length === 0 ? (
          <p className="text-sm text-zinc-500">{t.affiliate.emptyReferrals}</p>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-emerald-100/70 dark:border-emerald-900/50">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-emerald-100/80 bg-zinc-50/80 dark:border-emerald-900/40 dark:bg-zinc-900/50">
                <tr>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                    <span className="inline-flex items-center">
                      {t.affiliate.referredUserIdColumn}
                      <HelpTooltip content={t.affiliate.referredUserIdTooltip} position="right" />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.attributedAt}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.source}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.commissionsCount}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.totalCommissionCents}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.lastCommissionAt}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.hasPaidBadge}</th>
                </tr>
              </thead>
              <tbody>
                {(referrals ?? []).map((r) => (
                  <tr key={`${r.referred_user_id}-${r.attributed_at}`} className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 font-mono text-zinc-800 dark:text-zinc-200">{r.referred_user_id}</td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                      {r.attributed_at ? new Date(r.attributed_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{r.source}</td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{r.commissions_count}</td>
                    <td className="px-4 py-3 font-medium text-zinc-900 dark:text-white">
                      {fmt(r.total_commission_cents, cur)}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">
                      {r.last_commission_at ? new Date(r.last_commission_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium dark:bg-zinc-800">
                        {r.has_paid_commission ? t.common.yes : t.common.no}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        {t.affiliate.connectNote}:{' '}
        <a
          href="/legal/affiliate-program"
          className="inline-flex items-center gap-1 text-emerald-600 hover:underline dark:text-emerald-400"
        >
          {t.affiliate.docsLink}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </p>

      <div>
        <h2 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-white">{t.affiliate.commissionsTitle}</h2>
        {(rows ?? []).length === 0 ? (
          <p className="text-sm text-zinc-500">{t.affiliate.emptyCommissions}</p>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-emerald-100/70 dark:border-emerald-900/50">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead className="border-b border-emerald-100/80 bg-zinc-50/80 dark:border-emerald-900/40 dark:bg-zinc-900/50">
                <tr>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.common.date}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">
                    <span className="inline-flex items-center">
                      {t.affiliate.referredUserIdColumn}
                      <HelpTooltip content={t.affiliate.referredUserIdTooltip} position="right" />
                    </span>
                  </th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.plan}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.amount}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.common.status}</th>
                  <th className="px-4 py-3 font-medium text-zinc-600 dark:text-zinc-300">{t.affiliate.payableAt}</th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).map((r) => (
                  <tr key={r.public_id} className="border-b border-zinc-100 dark:border-zinc-800">
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 font-mono text-zinc-800 dark:text-zinc-200">{r.referred_user_id}</td>
                    <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">{r.plan_slug || '—'}</td>
                    <td className="px-4 py-3 font-medium text-zinc-900 dark:text-white">
                      {fmt(r.commission_amount_cents, r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium dark:bg-zinc-800">
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-600">
                      {r.payable_at ? new Date(r.payable_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
